<template>
  <div class="deploy-vm">
    <a-page-header title="Sanal Makine Oluştur" @back="() => $router.go(-1)" />
    <a-spin :spinning="loading">
      <a-row :gutter="24" v-if="app">
        <!-- Sol: Wizard -->
        <a-col :span="16">
          <a-card>
            <a-steps :current="currentStep" style="margin-bottom: 32px">
              <a-step title="Sunucu Detayları" />
              <a-step title="Ağ Ayarları" />
              <a-step title="Ekstra Hizmetler" />
              <a-step title="Onay" />
            </a-steps>

            <!-- Step 0: Sunucu Detayları -->
            <div v-show="currentStep === 0" class="step-content">
              <a-form layout="vertical">
                <a-form-item label="Sunucu Adı" required>
                  <a-input v-model:value="form.name" placeholder="Örn: web-server-01" />
                </a-form-item>
                <a-row :gutter="16">
                  <a-col :span="8">
                    <a-form-item label="CPU (Çekirdek)" required>
                      <a-input-number v-model:value="form.cpuNumber" :min="1" :max="128" style="width: 100%" placeholder="Örn: 2" />
                    </a-form-item>
                  </a-col>
                  <a-col :span="8">
                    <a-form-item label="RAM (GB)" required>
                      <a-input-number v-model:value="form.ramGB" :min="1" :max="512" style="width: 100%" placeholder="Örn: 4" />
                    </a-form-item>
                  </a-col>
                  <a-col :span="8">
                    <a-form-item label="Disk Boyutu (GB)" required>
                      <a-input-number v-model:value="form.diskGB" :min="10" :max="10000" style="width: 100%" placeholder="Örn: 50" />
                    </a-form-item>
                  </a-col>
                </a-row>
                <a-alert
                  message="CPU, RAM ve Disk değerlerini ihtiyacınıza göre belirleyebilirsiniz."
                  type="info"
                  show-icon />
              </a-form>
            </div>

            <!-- Step 1: Ağ Ayarları -->
            <div v-show="currentStep === 1" class="step-content">
              <a-form layout="vertical">
                <a-spin :spinning="networkLoading || templateZonesLoading">
                  <a-alert
                    v-if="templateChecked && !templateFound"
                    type="warning"
                    show-icon
                    style="margin-bottom: 12px"
                    message="Bu ürünün template'i kullanılamıyor"
                    :description="`Ürünün template'i (ID: ${app.template_id || 'tanımsız'}) hesabınız için hiçbir zone'da kullanılabilir değil. Template'in Public olduğundan ve ilgili zone'lara kopyalandığından emin olun ya da ürün tanımındaki Template ID'yi güncelleyin.`" />
                  <a-alert
                    v-else-if="templateChecked && templateFound && allNetworks.length > 0 && compatibleNetworks.length === 0"
                    type="warning"
                    show-icon
                    style="margin-bottom: 12px"
                    message="Bu ürün için uygun zone'da ağınız yok"
                    :description="`Bu ürün şu zone'larda kurulabilir: ${templateZoneNames.join(', ')}. Bu zone'larda size ait bir ağ bulunmuyor; önce bu zone'lardan birinde bir ağ oluşturun.`">
                    <template #action>
                      <a-button size="small" type="primary" @click="goCreateNetwork">Ağ Oluştur</a-button>
                    </template>
                  </a-alert>
                  <a-alert
                    v-else-if="templateChecked && templateFound && incompatibleNetworks.length > 0"
                    type="info"
                    show-icon
                    style="margin-bottom: 12px"
                    :message="`Bu ürün şu zone'larda kurulabilir: ${templateZoneNames.join(', ')}. Diğer zone'lardaki ağlar seçilemez.`" />
                  <a-form-item label="Network Seçiniz" required>
                    <a-radio-group v-model:value="form.networkId" @change="onNetworkChange" style="width: 100%">
                      <div v-if="allNetworks.length > 0" class="network-list">
                        <div
                          v-for="net in sortedNetworks"
                          :key="net.id"
                          class="network-item"
                          :class="{ selected: form.networkId === net.id, disabled: !isNetworkCompatible(net) }">
                          <a-radio :value="net.id" :disabled="!isNetworkCompatible(net)">
                            <span class="network-name">{{ net.name }}</span>
                            <a-tag size="small" :color="net.type === 'L2' ? 'blue' : 'green'" style="margin-left: 8px">{{ net.type }}</a-tag>
                            <a-tag v-if="net.zonename" size="small" style="margin-left: 4px">{{ net.zonename }}</a-tag>
                            <span v-if="!isNetworkCompatible(net)" class="network-incompatible">
                              Bu ürün {{ net.zonename || 'bu zone' }} için uygun değil (template bu zone'da yok)
                            </span>
                          </a-radio>
                        </div>
                      </div>
                      <a-empty v-else description="Kullanılabilir ağ bulunamadı">
                        <a-button type="primary" @click="goCreateNetwork">Ağ Oluştur</a-button>
                      </a-empty>
                    </a-radio-group>
                  </a-form-item>
                </a-spin>

                <div v-if="isL2Network">
                  <a-divider style="margin: 12px 0" />
                  <a-row :gutter="16">
                    <a-col :span="8">
                      <a-form-item label="IP Adresi" required>
                        <a-input v-model:value="form.ipAddress" placeholder="Örn: 192.168.1.10" />
                      </a-form-item>
                    </a-col>
                    <a-col :span="8">
                      <a-form-item label="Subnet" required>
                        <a-input v-model:value="form.subnet" placeholder="Örn: 255.255.255.0" />
                      </a-form-item>
                    </a-col>
                    <a-col :span="8">
                      <a-form-item label="Gateway" required>
                        <a-input v-model:value="form.gateway" placeholder="Örn: 192.168.1.1" />
                      </a-form-item>
                    </a-col>
                  </a-row>
                </div>
              </a-form>
            </div>

            <!-- Step 2: Ekstra Hizmetler -->
            <div v-show="currentStep === 2" class="step-content">
              <a-form layout="vertical">
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <CloudServerOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Backup</div>
                      <div class="extra-service-desc">Sunucunuzun düzenli yedeklerini alarak veri kaybına karşı koruma sağlar.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.backupEnabled" />
                </div>
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <AppstoreAddOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Disaster Recovery</div>
                      <div class="extra-service-desc">Felaket durumunda sunucunuzun farklı bir lokasyonda kopyasını çalıştırarak kesintisiz hizmet sunar.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.drEnabled" />
                </div>
                <div class="extra-service-item">
                  <div class="extra-service-info">
                    <DashboardOutlined class="extra-service-icon" />
                    <div>
                      <div class="extra-service-name">Monitoring</div>
                      <div class="extra-service-desc">Sunucunuzun CPU, RAM, disk ve ağ kullanımını anlık olarak izleyerek performans sorunlarını erken tespit eder.</div>
                    </div>
                  </div>
                  <a-switch v-model:checked="form.monitoringEnabled" />
                </div>
              </a-form>
            </div>

            <!-- Step 3: Onay -->
            <div v-show="currentStep === 3" class="step-content">
              <a-alert
                v-if="offeringFallbackNote"
                :message="offeringFallbackNote"
                type="warning"
                show-icon
                style="margin-bottom: 16px" />
              <a-descriptions title="Kurulum Özeti" bordered :column="1">
                <a-descriptions-item label="Ürün">{{ app.name }}</a-descriptions-item>
                <a-descriptions-item label="Kategori">{{ app.category_name || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Versiyon">{{ app.product_version || '-' }}</a-descriptions-item>
                <a-descriptions-item label="İşletim Sistemi">{{ app.product_os || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Template">
                  {{ resolvedTemplate ? resolvedTemplate.name : (app.template_id || '-') }}
                </a-descriptions-item>
                <a-descriptions-item label="Sunucu Adı">{{ form.name || '-' }}</a-descriptions-item>
                <a-descriptions-item label="CPU">{{ form.cpuNumber ? form.cpuNumber + ' Çekirdek' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="RAM">{{ form.ramGB ? form.ramGB + ' GB' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="Disk">{{ form.diskGB ? form.diskGB + ' GB' : '-' }}</a-descriptions-item>
                <a-descriptions-item label="Ağ">{{ form.networkName || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Zone">{{ zoneName || zoneId || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Ağ Tipi">{{ selectedNetworkType || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="IP Adresi">{{ form.ipAddress || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="Subnet">{{ form.subnet || '-' }}</a-descriptions-item>
                <a-descriptions-item v-if="isL2Network" label="Gateway">{{ form.gateway || '-' }}</a-descriptions-item>
                <a-descriptions-item label="Backup">
                  <a-tag :color="form.backupEnabled ? 'green' : 'default'">{{ form.backupEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="Disaster Recovery">
                  <a-tag :color="form.drEnabled ? 'green' : 'default'">{{ form.drEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
                <a-descriptions-item label="Monitoring">
                  <a-tag :color="form.monitoringEnabled ? 'green' : 'default'">{{ form.monitoringEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
                </a-descriptions-item>
              </a-descriptions>
            </div>

            <!-- Navigation -->
            <div class="step-actions">
              <a-button v-if="currentStep > 0" @click="currentStep--">
                Geri
              </a-button>
              <a-button v-if="currentStep < 3" type="primary" @click="nextStep">
                İleri
              </a-button>
              <a-button v-if="currentStep === 3" type="primary" :loading="deploying || offeringLoading" @click="handleDeploy">
                <template #icon><CloudServerOutlined /></template>
                Sanal Makineyi Oluştur
              </a-button>
            </div>
          </a-card>
        </a-col>

        <!-- Sağ: Canlı Özet -->
        <a-col :span="8">
          <a-card class="summary-card">
            <template #title>
              <div class="summary-header">
                <img v-if="app.icon" :src="app.icon" :alt="app.name" class="summary-icon" />
                <AppstoreOutlined v-else style="font-size: 28px; color: #1890ff" />
                <span>{{ app.name }}</span>
              </div>
            </template>
            <div class="summary-section">
              <div class="summary-label">Kategori</div>
              <div class="summary-value">{{ app.category_name || '-' }}</div>
            </div>
            <div class="summary-section" v-if="app.product_version">
              <div class="summary-label">Versiyon</div>
              <div class="summary-value">{{ app.product_version }}</div>
            </div>
            <div class="summary-section" v-if="app.product_os">
              <div class="summary-label">İşletim Sistemi</div>
              <div class="summary-value">{{ app.product_os }}</div>
            </div>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Sunucu Adı</div>
              <div class="summary-value" :class="{ placeholder: !form.name }">{{ form.name || 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">CPU</div>
              <div class="summary-value" :class="{ placeholder: !form.cpuNumber }">{{ form.cpuNumber ? form.cpuNumber + ' Çekirdek' : 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">RAM</div>
              <div class="summary-value" :class="{ placeholder: !form.ramGB }">{{ form.ramGB ? form.ramGB + ' GB' : 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Disk</div>
              <div class="summary-value" :class="{ placeholder: !form.diskGB }">{{ form.diskGB ? form.diskGB + ' GB' : 'Belirtilmedi' }}</div>
            </div>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Ağ</div>
              <div class="summary-value" :class="{ placeholder: !form.networkName }">{{ form.networkName || 'Belirtilmedi' }}</div>
            </div>
            <div class="summary-section" v-if="selectedNetworkType">
              <div class="summary-label">Ağ Tipi</div>
              <div class="summary-value">
                <a-tag size="small" :color="selectedNetworkType === 'L2' ? 'blue' : 'green'">{{ selectedNetworkType }}</a-tag>
              </div>
            </div>
            <template v-if="isL2Network">
              <div class="summary-section">
                <div class="summary-label">IP Adresi</div>
                <div class="summary-value" :class="{ placeholder: !form.ipAddress }">{{ form.ipAddress || 'Belirtilmedi' }}</div>
              </div>
              <div class="summary-section">
                <div class="summary-label">Subnet</div>
                <div class="summary-value" :class="{ placeholder: !form.subnet }">{{ form.subnet || 'Belirtilmedi' }}</div>
              </div>
              <div class="summary-section">
                <div class="summary-label">Gateway</div>
                <div class="summary-value" :class="{ placeholder: !form.gateway }">{{ form.gateway || 'Belirtilmedi' }}</div>
              </div>
            </template>
            <a-divider style="margin: 12px 0" />
            <div class="summary-section">
              <div class="summary-label">Backup</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.backupEnabled ? 'green' : 'default'">{{ form.backupEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Disaster Recovery</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.drEnabled ? 'green' : 'default'">{{ form.drEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
            <div class="summary-section">
              <div class="summary-label">Monitoring</div>
              <div class="summary-value">
                <a-tag size="small" :color="form.monitoringEnabled ? 'green' : 'default'">{{ form.monitoringEnabled ? 'Aktif' : 'Pasif' }}</a-tag>
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </a-spin>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { csPortalApi } from '@/utils/csPortal'
import { addPendingMpRequest, removePendingMpRequest } from '@/utils/mpRequestSync'
import { message, notification } from 'ant-design-vue'
import { getAPI, postAPI } from '@/api'
import {
  AppstoreOutlined,
  AppstoreAddOutlined,
  CloudServerOutlined,
  DashboardOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'MarketPlaceDeployVM',
  components: {
    AppstoreOutlined,
    AppstoreAddOutlined,
    CloudServerOutlined,
    DashboardOutlined
  },
  setup () {
    const route = useRoute()
    const router = useRouter()
    const instance = getCurrentInstance()
    // cs-portal API adresi config.json (csPortalUrl) / Docker CS_PORTAL_URL uzerinden gelir
    const CS_PORTAL_API = csPortalApi()
    const app = ref(null)
    const loading = ref(false)
    const deploying = ref(false)
    const currentStep = ref(0)
    const networkLoading = ref(false)
    const allNetworks = ref([])

    // Service offering'ler: zone/domain bagimsiz tek seferde cekilir, secim istemci tarafinda yapilir
    const offerings = ref([])
    const offeringLoading = ref(false)

    // Template cozumu: yalnizca cs-portal'daki template_id kullanilir (ada gore eslestirme YOK).
    // listTemplates(id=...) ile bu ID'nin hazir oldugu zone'lar bulunur; network secimi buna gore filtrelenir.
    // Template her zone'da ayni UUID ile gorunur (Public template kurali) - farkli UUID senaryosu desteklenmez.
    const templateZonesLoading = ref(false)
    const templateChecked = ref(false) // kontrol tamamlandi mi
    const templateEntries = ref([]) // listTemplates(id) sonucu: zone basina kayit

    const form = reactive({
      name: '',
      cpuNumber: null,
      ramGB: null,
      diskGB: null,
      networkId: null,
      networkName: '',
      ipAddress: '',
      gateway: '',
      subnet: '',
      backupEnabled: false,
      drEnabled: false,
      monitoringEnabled: false
    })

    // Secili network (tek kaynak): zone, tip ve isim her zaman buradan turetilir
    const selectedNetwork = computed(() => {
      if (!form.networkId) return null
      return allNetworks.value.find(n => n.id === form.networkId) || null
    })

    const selectedNetworkType = computed(() => selectedNetwork.value ? selectedNetwork.value.type : null)

    // Zone = secili network'un zone'u. Event'e bagli degil; network degisince otomatik guncellenir,
    // boylece deploy istegine asla eski/farkli bir zoneid gitmez.
    const zoneId = computed(() => selectedNetwork.value ? selectedNetwork.value.zoneid : null)
    const zoneName = computed(() => selectedNetwork.value ? selectedNetwork.value.zonename : null)

    const isL2Network = computed(() => {
      return selectedNetworkType.value === 'L2'
    })

    const fetchProduct = async () => {
      loading.value = true
      try {
        const slug = route.params.slug
        const res = await axios.get(`${CS_PORTAL_API}/marketplace/products/${slug}`)
        app.value = res.data
        fetchTemplateZones()
      } catch (error) {
        console.error('Error fetching product:', error)
        message.error('Ürün bilgisi yüklenemedi')
      } finally {
        loading.value = false
      }
    }

    // Urunun template_id'sinin hazir oldugu zone'lari getirir (ayni UUID birden fazla zone'da olabilir; zone basina kayit doner)
    const fetchTemplateZones = async () => {
      templateChecked.value = false
      templateEntries.value = []
      const templateId = app.value && app.value.template_id
      if (!templateId) {
        templateChecked.value = true
        return
      }
      templateZonesLoading.value = true
      try {
        const res = await getAPI('listTemplates', {
          templatefilter: 'executable',
          id: templateId,
          listall: true
        })
        templateEntries.value = (res.listtemplatesresponse.template || [])
          .filter(t => t.zoneid && (t.isready === undefined || t.isready === true))
        console.debug('[marketplace] template zones:', templateId, templateEntries.value.map(t => t.zonename))
      } catch (error) {
        const status = error?.response?.status
        const data = error?.response?.data || {}
        const body = data[Object.keys(data).find(k => k.includes('response')) || ''] || {}
        const text = String(body.errortext || error?.message || '')
        if (status === 431 || body.errorcode === 431 || /does not exist/i.test(text)) {
          // ID gecerli bir template degil -> template yok
          console.warn('[marketplace] template bulunamadi:', templateId, text)
          templateEntries.value = []
        } else {
          // Gecici hata: filtre uygulanmaz, kullaniciyi engellemeyelim
          console.warn('listTemplates error:', error)
          templateEntries.value = []
          templateChecked.value = false
          templateZonesLoading.value = false
          return
        }
      }
      templateZonesLoading.value = false
      templateChecked.value = true
    }

    const templateByZone = computed(() => {
      const map = {}
      for (const t of templateEntries.value) {
        map[t.zoneid] = { id: t.id, name: t.name, zoneid: t.zoneid, zonename: t.zonename }
      }
      return map
    })

    const templateFound = computed(() => templateEntries.value.length > 0)
    const templateZones = computed(() => Object.keys(templateByZone.value).map(id => ({ id, name: templateByZone.value[id].zonename || id })))
    const templateZoneIds = computed(() => new Set(Object.keys(templateByZone.value)))
    const templateZoneNames = computed(() => templateZones.value.map(z => z.name))

    // Secili zone icin template kaydi (gosterim amacli; deploy her zaman app.template_id ile yapilir)
    const resolvedTemplate = computed(() => (zoneId.value && templateByZone.value[zoneId.value]) || null)

    // Filtre yalnizca kontrol tamamlandi ve template en az bir zone'da bulunduysa uygulanir
    const isNetworkCompatible = (net) => {
      if (!templateChecked.value || !templateFound.value) return true
      return templateZoneIds.value.has(net.zoneid)
    }

    const compatibleNetworks = computed(() => allNetworks.value.filter(isNetworkCompatible))
    const incompatibleNetworks = computed(() => allNetworks.value.filter(n => !isNetworkCompatible(n)))
    // Uyumlu aglar once
    const sortedNetworks = computed(() => [...compatibleNetworks.value, ...incompatibleNetworks.value])

    const goCreateNetwork = () => {
      router.push({ path: '/guestnetwork' })
    }

    const fetchNetworks = () => {
      networkLoading.value = true
      getAPI('listNetworks', {
        listall: true
      }).then(response => {
        allNetworks.value = response.listnetworksresponse.network || []
      }).catch(() => {
        message.error('Ağ listesi yüklenemedi')
        allNetworks.value = []
      }).finally(() => {
        networkLoading.value = false
      })
    }

    const idListIncludes = (csv, id) => {
      if (!csv) return true // kisit yok
      if (!id) return true
      return String(csv).split(',').map(x => x.trim()).includes(id)
    }

    // Tum (sistem disi) service offering'leri getirir. Zone filtresi API'ye verilmez;
    // zone/domain uygunlugu istemci tarafinda degerlendirilir (farkli zone/domain/network'lerde calisabilsin).
    const fetchOfferings = async () => {
      offeringLoading.value = true
      try {
        const response = await getAPI('listServiceOfferings', {
          issystem: false,
          listall: true,
          page: 1,
          pagesize: 500
        })
        offerings.value = response.listserviceofferingsresponse.serviceoffering || []
        console.debug('[marketplace] service offerings:', offerings.value.map(o => ({
          name: o.name, id: o.id, custom: !!o.iscustomized, cpu: o.cpunumber, memory: o.memory, zoneid: o.zoneid, domainid: o.domainid, state: o.state
        })))
      } catch (error) {
        console.error('listServiceOfferings error:', error)
        offerings.value = []
      } finally {
        offeringLoading.value = false
      }
    }

    // Secilen zone'da kullanilabilecek offering'ler (zone kisiti olmayanlar + bu zone'a izinli olanlar, aktif olanlar)
    const usableOfferings = computed(() => {
      return offerings.value.filter(o => {
        if (o.state && String(o.state).toLowerCase() === 'inactive') return false
        return idListIncludes(o.zoneid, zoneId.value)
      })
    })

    const userDomainId = computed(() => instance.proxy.$store?.getters?.userInfo?.domainid || null)

    // Tercih puani: domain kisiti yok/uygun -> 0, aksi halde +5 (CloudStack domain kontrolunu kendisi yapar)
    const offeringScore = (o) => (idListIncludes(o.domainid, userDomainId.value) ? 0 : 5)

    const isCustom = (o) => !!o.iscustomized || (o.cpunumber == null && o.memory == null)

    const fmtRam = (mb) => `${Math.round((mb || 0) / 1024 * 10) / 10} GB`

    // Otomatik secim (arka planda): 1) custom offering  2) CPU/RAM birebir eslesen sabit plan
    // 3) en yakin buyuk sabit plan  4) en buyuk sabit plan. Kullaniciya plan secimi sorulmaz.
    const pickOffering = () => {
      const cpu = Number(form.cpuNumber) || 0
      const ramMB = (Number(form.ramGB) || 0) * 1024
      const list = usableOfferings.value
      if (list.length === 0) return null

      const byScore = (a, b) => offeringScore(a) - offeringScore(b)

      const customs = list.filter(isCustom).sort(byScore)
      if (customs.length) return { offering: customs[0], reason: 'custom' }

      const fixed = list.filter(o => !isCustom(o))
      const exact = fixed.filter(o => o.cpunumber === cpu && o.memory === ramMB).sort(byScore)
      if (exact.length) return { offering: exact[0], reason: 'exact' }

      const larger = fixed
        .filter(o => (o.cpunumber || 0) >= cpu && (o.memory || 0) >= ramMB)
        .sort((a, b) => {
          const da = (a.cpunumber - cpu) + (a.memory - ramMB) / 1024
          const db = (b.cpunumber - cpu) + (b.memory - ramMB) / 1024
          return da - db || byScore(a, b)
        })
      if (larger.length) return { offering: larger[0], reason: 'larger' }

      const biggest = [...fixed].sort((a, b) => ((b.cpunumber || 0) - (a.cpunumber || 0)) || ((b.memory || 0) - (a.memory || 0)))
      if (biggest.length) return { offering: biggest[0], reason: 'biggest' }
      return null
    }

    // Template zone bilgisi sonradan gelirse ve secili ag uyumsuzsa secimi temizle
    watch([templateByZone, templateChecked], () => {
      if (form.networkId && selectedNetwork.value && !isNetworkCompatible(selectedNetwork.value)) {
        form.networkId = null
        form.networkName = ''
      }
    })

    const resolvedOffering = computed(() => pickOffering())
    const selectedOffering = computed(() => resolvedOffering.value ? resolvedOffering.value.offering : null)

    // Sadece girilen CPU/RAM birebir uygulanamayacaksa (custom plan yok) kullaniciyi bilgilendir
    const offeringFallbackNote = computed(() => {
      const r = resolvedOffering.value
      if (!r || r.reason === 'custom' || r.reason === 'exact') return ''
      const o = r.offering
      return `Hesabınızda Custom Offering bulunamadığı için girdiğiniz ${form.cpuNumber} vCPU / ${form.ramGB} GB yerine ` +
        `"${o.name}" planı (${o.cpunumber} vCPU, ${fmtRam(o.memory)}) kullanılacaktır.`
    })

    const onNetworkChange = (e) => {
      const net = allNetworks.value.find(n => n.id === e.target.value)
      form.networkName = net ? net.name : ''
      form.ipAddress = ''
      form.gateway = ''
      form.subnet = ''
    }

    const nextStep = () => {
      if (currentStep.value === 0) {
        if (!form.name || !form.name.trim()) {
          message.warning('Sunucu adı zorunludur')
          return
        }
        if (!form.cpuNumber || form.cpuNumber < 1) {
          message.warning('CPU değeri giriniz')
          return
        }
        if (!form.ramGB || form.ramGB < 1) {
          message.warning('RAM değeri giriniz')
          return
        }
        if (!form.diskGB || form.diskGB < 10) {
          message.warning('Disk boyutu en az 10 GB olmalıdır')
          return
        }
      }
      if (currentStep.value === 1) {
        if (!form.networkId) {
          message.warning('Bir ağ seçiniz')
          return
        }
        if (selectedNetwork.value && !isNetworkCompatible(selectedNetwork.value)) {
          message.warning(`Seçilen ağın zone'unda (${selectedNetwork.value.zonename || '-'}) bu ürünün template'i yok. Uygun zone: ${templateZoneNames.value.join(', ')}`)
          return
        }
        if (isL2Network.value) {
          if (!form.ipAddress || !form.ipAddress.trim()) {
            message.warning('IP adresi zorunludur')
            return
          }
          if (!form.subnet || !form.subnet.trim()) {
            message.warning('Subnet zorunludur')
            return
          }
          if (!form.gateway || !form.gateway.trim()) {
            message.warning('Gateway zorunludur')
            return
          }
        }
      }
      currentStep.value++
    }

    let mpRequestId = null

    const handleDeploy = async () => {
      if (!zoneId.value) {
        message.error('Zone bilgisi bulunamadı, lütfen bir ağ seçiniz')
        return
      }
      const templateId = (app.value && app.value.template_id) || null
      if (!templateId) {
        message.error('Bu ürün için Template ID tanımlı değil')
        return
      }

      deploying.value = true
      // Offering listesi henuz gelmediyse bekle
      if (offerings.value.length === 0) {
        await fetchOfferings()
      }
      const offering = selectedOffering.value
      if (!offering) {
        deploying.value = false
        message.error('Hesabınızın kullanabileceği bir Service Offering bulunamadı')
        return
      }
      const useCustom = isCustom(offering)
      // Gercekte kullanilacak kaynak degerleri (sabit planda plandan gelir)
      const effectiveCpu = useCustom ? form.cpuNumber : (offering.cpunumber || form.cpuNumber)
      const effectiveRamMB = useCustom ? form.ramGB * 1024 : (offering.memory || form.ramGB * 1024)

      // 1. cs-portal'a talep kaydı oluştur (fire-and-forget)
      try {
        const requestData = {
          product_id: app.value.id,
          product_name: app.value.name,
          vm_name: form.name,
          cpu: effectiveCpu,
          ram: effectiveRamMB,
          service_offering_id: offering.id,
          service_offering_name: offering.name,
          disk: form.diskGB,
          network_name: form.networkName,
          network_type: selectedNetworkType.value,
          ip_address: form.ipAddress || null,
          subnet: form.subnet || null,
          gateway: form.gateway || null,
          backup_enabled: form.backupEnabled,
          dr_enabled: form.drEnabled,
          monitoring_enabled: form.monitoringEnabled,
          template_id: templateId,
          zone_id: zoneId.value,
          requested_by: instance.proxy.$store?.getters?.userInfo?.username || 'unknown',
          domain_name: instance.proxy.$store?.getters?.userInfo?.domain || null
        }
        const mpRes = await axios.post(`${CS_PORTAL_API}/marketplace/requests`, requestData)
        mpRequestId = mpRes.data.id
        console.log('MP Talep created, id:', mpRequestId)
      } catch (err) {
        console.warn('cs-portal talep kaydı oluşturulamadı, deploy devam ediyor:', err.message)
      }

      // 2. CloudStack deploy başlat
      const deployData = {
        name: form.name,
        displayname: form.name,
        zoneid: zoneId.value,
        templateid: templateId,
        serviceofferingid: offering.id,
        rootdisksize: form.diskGB,
        startvm: true,
        'iptonetworklist[0].networkid': form.networkId
      }

      // Custom offering: CPU/RAM degerleri details ile verilir (cpuSpeed plandan, yoksa 1000)
      if (useCustom) {
        deployData['details[0].cpuNumber'] = form.cpuNumber
        deployData['details[0].cpuSpeed'] = offering.cpuspeed || 1000
        deployData['details[0].memory'] = form.ramGB * 1024
      }

      if (isL2Network.value && form.ipAddress) {
        deployData['iptonetworklist[0].ip'] = form.ipAddress
      }

      console.log('Deploy VM data:', deployData, '| network:', selectedNetwork.value && {
        id: selectedNetwork.value.id, name: selectedNetwork.value.name, zoneid: selectedNetwork.value.zoneid, zonename: selectedNetwork.value.zonename
      })

      postAPI('deployVirtualMachine', deployData).then(response => {
        const jobId = response.deployvirtualmachineresponse.jobid
        const vmId = response.deployvirtualmachineresponse.id || null

        // cs-portal status → creating (job id ile; portal durumu CloudStack'ten kendisi de takip edebilsin)
        if (mpRequestId) {
          // Sekme kapatilirsa bile sonucun islenebilmesi icin bekleyen isi kaydet
          addPendingMpRequest(mpRequestId, jobId)
          axios.put(`${CS_PORTAL_API}/marketplace/requests/${mpRequestId}/status`, {
            status: 'creating',
            cloudstack_job_id: jobId,
            cloudstack_vm_id: vmId
          }).catch(err => console.warn('cs-portal status güncellenemedi:', err.message))
        }

        // Hemen instances sayfasına yönlendir
        router.push({ name: 'vm' })

        instance.proxy.$pollJob({
          jobId: jobId,
          title: 'Sanal Makine Oluştur',
          description: form.name,
          successMessage: 'Sanal makine başarıyla oluşturuldu',
          successMethod: (result) => {
            deploying.value = false
            // cs-portal status → deployed
            if (mpRequestId) {
              const reqId = mpRequestId
              const vmId = result?.jobresult?.virtualmachine?.id || null
              axios.put(`${CS_PORTAL_API}/marketplace/requests/${reqId}/status`, {
                status: 'deployed',
                cloudstack_vm_id: vmId
              }).then(() => removePendingMpRequest(reqId))
                .catch(err => console.warn('cs-portal status güncellenemedi (sync tekrar deneyecek):', err.message))
            }
          },
          errorMessage: 'Sanal makine oluşturulamadı',
          errorMethod: (result) => {
            deploying.value = false
            // 3. cs-portal status → failed (+ CloudStack hata metni)
            if (mpRequestId) {
              const reqId = mpRequestId
              const jobErr = result?.jobresult?.errortext || result?.jobresult?.errorcode || 'CloudStack job başarısız'
              axios.put(`${CS_PORTAL_API}/marketplace/requests/${reqId}/status`, {
                status: 'failed',
                error_message: String(jobErr)
              }).then(() => removePendingMpRequest(reqId))
                .catch(err => console.warn('cs-portal status güncellenemedi (sync tekrar deneyecek):', err.message))
            }
          },
          catchMethod: () => {
            deploying.value = false
          }
        })
      }).catch(error => {
        deploying.value = false
        console.error('deployVirtualMachine error:', error)
        const responseData = error?.response?.data || error?.data || {}
        const csResponse = responseData?.deployvirtualmachineresponse || {}
        console.error('CloudStack error:', JSON.stringify(csResponse))
        const errorText = csResponse?.errortext || error?.message || 'Bilinmeyen hata'
        notification.error({
          message: 'Sanal Makine Oluşturulamadı',
          description: errorText
        })
        // cs-portal status → failed (+ CloudStack hata metni)
        if (mpRequestId) {
          axios.put(`${CS_PORTAL_API}/marketplace/requests/${mpRequestId}/status`, {
            status: 'failed',
            error_message: errorText
          }).catch(err => console.warn('cs-portal status güncellenemedi:', err.message))
        }
      })
    }

    onMounted(() => {
      fetchProduct()
      fetchNetworks()
      fetchOfferings()
    })

    return {
      app,
      loading,
      deploying,
      currentStep,
      networkLoading,
      allNetworks,
      selectedNetworkType,
      zoneId,
      zoneName,
      isL2Network,
      form,
      onNetworkChange,
      nextStep,
      handleDeploy,
      offeringLoading,
      selectedOffering,
      offeringFallbackNote,
      templateZonesLoading,
      templateChecked,
      templateFound,
      templateZoneNames,
      resolvedTemplate,
      isNetworkCompatible,
      compatibleNetworks,
      incompatibleNetworks,
      sortedNetworks,
      goCreateNetwork
    }
  }
}
</script>

<style scoped>
.step-content {
  min-height: 250px;
  padding: 24px 0;
}
.step-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

/* Sağ Özet Panel */
.summary-card {
  position: sticky;
  top: 80px;
}
.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
}
.summary-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.summary-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}
.summary-label {
  color: #888;
  font-size: 13px;
}
.summary-value {
  font-weight: 600;
  font-size: 13px;
  color: #23272f;
}
.summary-value.placeholder {
  color: #d9d9d9;
  font-weight: 400;
}

/* Network Listesi */
.network-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.network-item {
  padding: 10px 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  transition: all 0.2s;
}
.network-item:hover {
  border-color: #1890ff;
  background: #f0f7ff;
}
.network-item.selected {
  border-color: #1890ff;
  background: #e6f4ff;
}
.network-name {
  font-weight: 500;
}
.network-item.disabled {
  opacity: 0.6;
  background: #fafafa;
}
.network-item.disabled:hover {
  border-color: #f0f0f0;
  background: #fafafa;
}
.network-incompatible {
  margin-left: 8px;
  font-size: 12px;
  color: #fa8c16;
}

/* Ekstra Hizmetler */
.extra-service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s;
}
.extra-service-item:hover {
  border-color: #1890ff;
  background: #fafafa;
}
.extra-service-info {
  display: flex;
  align-items: center;
  gap: 14px;
}
.extra-service-icon {
  font-size: 28px;
  color: #1890ff;
}
.extra-service-name {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 4px;
}
.extra-service-desc {
  color: #888;
  font-size: 13px;
}
</style>
